import { type ReactNode } from "react";
import { Plus } from "lucide-react";
import { useTranslation } from "react-i18next";

import { WFPButton } from "@/components/ui/button";
import { Card, CardAction, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";

import type { DashboardQuantity, InfoHubDashboardData } from "../../types";

// Content-type values expected by the form/backend, keyed to each dashboard card.
export const CONTENT_TYPE = {
  aggregatorInfo: "Aggregator Information",
  farmerTraining: "Farmer Training",
  inputDistribution: "Input Distribution",
  equipmentDistribution: "Equipment Distribution",
} as const;

interface DashboardProps {
  data?: InfoHubDashboardData;
  isFetching?: boolean;
  onNewMessage?: (contentType: string) => void;
}

const CardHeaderWithNew = ({
  title,
  contentType,
  onNewMessage,
}: {
  title: string;
  contentType: string;
  onNewMessage?: (contentType: string) => void;
}) => {
  const { t } = useTranslation();
  return (
    <CardHeader>
      <CardTitle>{title}</CardTitle>
      {onNewMessage ? (
        <CardAction>
          <WFPButton
            type="button"
            variant="link"
            size="sm"
            className="h-auto p-0"
            onClick={() => onNewMessage(contentType)}
          >
            <Plus />
            {t("infoHub.dashboard.new")}
          </WFPButton>
        </CardAction>
      ) : null}
    </CardHeader>
  );
};

const MetricRow = ({ label, value }: { label: string; value: ReactNode }) => (
  <div className="flex items-baseline justify-between gap-3 py-1">
    <span className="text-muted-foreground text-sm">{label}</span>
    <span className="font-semibold text-foreground text-sm tabular-nums">{value}</span>
  </div>
);

// "120 kg, 30 bags" — or "0" when there is nothing to show.
const formatQuantities = (quantities?: DashboardQuantity[]) => {
  if (!quantities || quantities.length === 0) {
    return "0";
  }
  return quantities.map(quantity => `${quantity.total} ${quantity.unit}`).join(", ");
};

export const Dashboard = ({ data, isFetching = false, onNewMessage }: DashboardProps) => {
  const { t } = useTranslation();

  if (isFetching) {
    return (
      <div className="flex justify-center py-8">
        <Spinner className="size-6 text-primary" />
      </div>
    );
  }

  const aggregatorInfo = data?.aggregator_information;
  const farmerTraining = data?.farmer_training;
  const inputDistribution = data?.input_distribution;
  const equipmentDistribution = data?.equipment_distribution;

  return (
    <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
      <Card>
        <CardHeaderWithNew
          title={t("infoHub.dashboard.aggregatorInfo")}
          contentType={CONTENT_TYPE.aggregatorInfo}
          onNewMessage={onNewMessage}
        />
        <CardContent>
          <MetricRow
            label={t("infoHub.dashboard.messagesSent")}
            value={aggregatorInfo?.messages_sent ?? 0}
          />
          <MetricRow
            label={t("infoHub.dashboard.messagesRead")}
            value={aggregatorInfo?.messages_read ?? 0}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeaderWithNew
          title={t("infoHub.dashboard.farmerTraining")}
          contentType={CONTENT_TYPE.farmerTraining}
          onNewMessage={onNewMessage}
        />
        <CardContent>
          <MetricRow
            label={t("infoHub.dashboard.messagesSent")}
            value={farmerTraining?.messages_sent ?? 0}
          />
          <MetricRow
            label={t("infoHub.dashboard.messagesRead")}
            value={farmerTraining?.messages_read ?? 0}
          />
          <MetricRow
            label={t("infoHub.dashboard.trainingEventsCreated")}
            value={farmerTraining?.training_events_created ?? 0}
          />
          <MetricRow
            label={t("infoHub.dashboard.farmersParticipated")}
            value={farmerTraining?.farmers_participated ?? 0}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeaderWithNew
          title={t("infoHub.dashboard.inputDistribution")}
          contentType={CONTENT_TYPE.inputDistribution}
          onNewMessage={onNewMessage}
        />
        <CardContent>
          <MetricRow
            label={t("infoHub.dashboard.messagesSent")}
            value={inputDistribution?.messages_sent ?? 0}
          />
          <MetricRow
            label={t("infoHub.dashboard.messagesRead")}
            value={inputDistribution?.messages_read ?? 0}
          />
          <MetricRow
            label={t("infoHub.dashboard.quantityAllocated")}
            value={formatQuantities(inputDistribution?.quantity_allocated_to_aggregators)}
          />
          <MetricRow
            label={t("infoHub.dashboard.quantityDistributed")}
            value={formatQuantities(inputDistribution?.quantity_distributed_to_farmers)}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeaderWithNew
          title={t("infoHub.dashboard.equipmentDistribution")}
          contentType={CONTENT_TYPE.equipmentDistribution}
          onNewMessage={onNewMessage}
        />
        <CardContent>
          <MetricRow
            label={t("infoHub.dashboard.messagesSent")}
            value={equipmentDistribution?.messages_sent ?? 0}
          />
          <MetricRow
            label={t("infoHub.dashboard.messagesRead")}
            value={equipmentDistribution?.messages_read ?? 0}
          />
          <MetricRow
            label={t("infoHub.dashboard.quantityAllocated")}
            value={equipmentDistribution?.quantity_allocated_to_aggregators ?? 0}
          />
          <MetricRow
            label={t("infoHub.dashboard.quantityDistributed")}
            value={equipmentDistribution?.quantity_distributed_to_farmers ?? 0}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
